/**
 * A change the memo chat proposes, as a before and after (DOP-618,
 * DOP-653), with "Apply to memo" and "Not now".
 *
 * Apply is the person's decision to change the memo, so it changes it: the
 * change is saved as the memo's next version, the memo on screen becomes
 * that version, and the card says which version it was saved as. The
 * version it was applied to stays as it was. Nothing is applied that the
 * person did not apply.
 *
 * The one exception is a person with edits of their own they have not
 * saved. Saving the change alone would leave those behind, and saving them
 * with it would save what they had not finished. There the change is put in
 * with their edits and edit mode opens on it, for them to save together.
 *
 * Every kind of change reads the same way: what it is for, what it says now,
 * what it would say, and the documents it names. A document opens where it
 * is cited, as a citation in the memo does, so a change can be checked before
 * it is applied.
 *
 * An answer that proposes several changes offers them together on its last
 * card: a section updated from newer financials is its prose, its table and
 * its figures, and applying them one at a time would mean saving a version
 * between each.
 */

import { stripMarks } from "./inlineMarks";
import { Check, Loader2 } from "lucide-react";
import React, { useState } from "react";

import { type DocumentSource, documentSourceLabel } from "./documentSource";
import MemoChart from "./MemoChart";
import type { PlacedEdit, PlacedSource, PlacedTable, PlacedValue } from "./memoEditProposal";
import { cellKey } from "./tableEditModel";

export interface MemoEditProposalCardProps {
	/** Null while the block is still arriving, or when it names nothing in this memo. */
	placed: PlacedEdit | null;
	/** True while the answer is still arriving. */
	pending: boolean;
	onApply: (placed: PlacedEdit) => void;
	/** Opens a document the change names, where it is cited. */
	onOpenSource?: (source: DocumentSource) => void;
	/** On the last card of an answer with several changes: apply them together. */
	applyAll?: { count: number; onApply: () => void } | null;
	/** The change was applied: the version it was saved as. */
	applied?: { version: number } | null;
	/** The change is being saved right now. */
	applying?: boolean;
	/**
	 * What Apply does: "save" the change as a new version, or put it in with
	 * the person's unsaved edits and open edit mode ("edit").
	 */
	mode?: "save" | "edit";
	/** The version on screen, which stays as it was. */
	baseVersion?: number | null;
}

const BEFORE = "rounded-md bg-[#fef3f2] px-2 py-1.5 text-[#8a9099] line-through";
const AFTER = "rounded-md bg-[#ecfdf3] px-2 py-1.5 text-[#1c1d1f]";
const LINK =
	"text-left text-[#004C4B] underline decoration-[#004C4B]/40 decoration-1 underline-offset-2 hover:decoration-[#004C4B]";

const SourceLink: React.FC<{
	source: PlacedSource;
	onOpenSource?: (source: DocumentSource) => void;
}> = ({ source, onOpenSource }) =>
	onOpenSource ? (
		<button
			type="button"
			onClick={() => onOpenSource(source.source)}
			className={LINK}
			data-testid="proposal-source"
		>
			{source.label}
		</button>
	) : (
		<span data-testid="proposal-source">{source.label}</span>
	);

/** The proposed table, the cells that would change marked. */
const ProposedTable: React.FC<{ placed: PlacedTable }> = ({ placed }) => {
	const { after, changed, before } = placed;
	const columns = Math.max(after.columns.length, ...after.rows.map((row) => row.length), 1);
	const newColumn = (c: number) => before !== null && c >= Math.max(before.columns.length, 1);
	return (
		<div className="overflow-x-auto rounded-lg border border-[#e7e7e4] font-sans">
			<table className="w-full border-collapse text-[12.5px] leading-5 text-[#1F2937]">
				{after.caption && (
					<caption className="px-2.5 pb-1 pt-2 text-left text-[12px] font-semibold text-[#3f4247]">
						{after.caption}
					</caption>
				)}
				{after.columns.some(Boolean) && (
					<thead>
						<tr className="border-b border-[#e5e7eb] bg-[#fafafa]">
							{Array.from({ length: columns }, (_, c) => (
								<th
									// Columns are positional within the proposed table.
									// eslint-disable-next-line react/no-array-index-key
									key={c}
									scope="col"
									className={`px-2.5 py-1.5 text-xs font-semibold text-[#5B6472] ${
										c === 0 ? "text-left" : "text-right"
									} ${newColumn(c) ? "bg-[#ecfdf3]" : ""}`}
								>
									{after.columns[c] ?? ""}
								</th>
							))}
						</tr>
					</thead>
				)}
				<tbody>
					{after.rows.map((row, r) => (
						// eslint-disable-next-line react/no-array-index-key
						<tr key={r} className="border-b border-[#f1f2f4] last:border-0">
							{Array.from({ length: columns }, (_, c) => (
								<td
									// eslint-disable-next-line react/no-array-index-key
									key={c}
									data-changed={changed.has(cellKey(r, c)) || undefined}
									className={`px-2.5 py-1.5 align-top ${c === 0 ? "text-left" : "text-right tabular-nums"} ${
										changed.has(cellKey(r, c)) ? "bg-[#ecfdf3]" : ""
									}`}
								>
									{row[c] || <span className="text-[#9a9ea3]">—</span>}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

/**
 * Values for the financial summary or the company facts, one a line. Once
 * applied, only what was put in is shown: the memo on screen has the change,
 * so what it "reads now" is the change itself, not what it replaced.
 */
const ProposedValues: React.FC<{
	values: PlacedValue[];
	onOpenSource?: (source: DocumentSource) => void;
	applied: boolean;
}> = ({ values, onOpenSource, applied }) => (
	<ul className="space-y-1.5 font-sans text-[12.5px] leading-5">
		{values.map((value, index) => (
			// A value is its place in the proposal: two may name one cell.
			// eslint-disable-next-line react/no-array-index-key
			<li key={index} className="rounded-md border border-[#eef0f2] px-2.5 py-1.5" data-testid="proposal-value">
				<span className="font-medium text-[#1c1d1f]">
					{value.label}
					{value.column ? ` · ${value.column}` : ""}
				</span>
				{applied ? (
					<span className="block font-semibold tabular-nums text-[#1c1d1f]">{value.after}</span>
				) : value.problem ? (
					<span className="block text-[#b54708]">{value.problem}</span>
				) : (
					<span className="block tabular-nums">
						<span className="text-[#8a9099] line-through">{value.before}</span>
						<span className="px-1 text-[#8a9099]" aria-hidden>
							→
						</span>
						<span className="sr-only"> becomes </span>
						<span className="rounded bg-[#ecfdf3] px-1 font-semibold text-[#1c1d1f]">{value.after}</span>
					</span>
				)}
				{(applied || !value.problem) && value.source && (
					<span className="block text-[11.5px] text-[#5B6472]">
						Source:{" "}
						<SourceLink
							source={{ label: documentSourceLabel(value.source), source: value.source }}
							onOpenSource={onOpenSource}
						/>
					</span>
				)}
				{!applied && !value.problem && !value.source && !value.note && (
					<span className="block text-[11.5px] text-[#b54708]">No source given</span>
				)}
				{!value.problem && value.note && (
					<span className="block text-[11.5px] text-[#6B7280]">{value.note}</span>
				)}
			</li>
		))}
	</ul>
);

const Body: React.FC<{
	placed: PlacedEdit;
	onOpenSource?: (source: DocumentSource) => void;
	/** The change is in the memo: show what was put in, not what it replaced. */
	applied: boolean;
}> = ({ placed, onOpenSource, applied }) => {
	if (placed.kind === "table") {
		if (applied) return <ProposedTable placed={{ ...placed, changed: new Set(), before: null }} />;
		return placed.problem && placed.changed.size === 0 && placed.before === null ? (
			<p className={`${AFTER} font-sans text-[12.5px]`}>
				{placed.after.caption ?? "A table"}: {placed.after.rows.length} rows
			</p>
		) : (
			<ProposedTable placed={placed} />
		);
	}
	if (placed.kind === "section") {
		// A section the memo does not have (DOP-656): its heading, then its paragraphs.
		return (
			<div className="px-3 py-2" data-testid="proposed-section">
				<p className="mb-1 text-[11px] font-bold uppercase tracking-[0.08em] text-[#004c4b]">{placed.proposal.title}</p>
				{placed.proposal.paragraphs.map((paragraph, index) => (
					// Positional: the proposal is immutable.
					// eslint-disable-next-line react/no-array-index-key
					<p key={index} className={AFTER}>
						{paragraph.lead ? (
							<>
								<strong>{paragraph.lead}</strong> {paragraph.sentences.join(" ")}
							</>
						) : (
							paragraph.sentences.join(" ")
						)}
					</p>
				))}
				<p className="mt-1 font-sans text-[11px] text-[#6B7280]">
					{placed.afterSection ? `After ${stripMarks(placed.afterSection.title)}.` : "At the end of the memo."}
				</p>
			</div>
		);
	}
	if (placed.kind === "chart") {
		// The chart as it would be drawn in the memo (DOP-655); the one it
		// redraws is named, not drawn twice.
		return (
			<div className="px-3 py-2" data-testid="proposed-chart">
				{placed.before && !applied && (
					<p className={`${BEFORE} font-sans text-[12px]`}>
						<span className="sr-only">Before: </span>
						Redraws {placed.before.title ? `“${placed.before.title}”` : "the chart"}
					</p>
				)}
				<MemoChart chart={placed.after} idPrefix={`proposal-${placed.after.title ?? "chart"}`} compact />
			</div>
		);
	}
	if (placed.kind === "figures" || placed.kind === "company_facts") {
		return <ProposedValues values={placed.values} onOpenSource={onOpenSource} applied={applied} />;
	}
	if (placed.kind === "title") {
		return (
			<>
				{!applied && (
					<p className={BEFORE}>
						<span className="sr-only">Before: </span>
						{placed.before}
					</p>
				)}
				<p className={AFTER}>
					<span className="sr-only">After: </span>
					{placed.after}
				</p>
			</>
		);
	}
	const { before, after, proposal } = placed;
	return (
		<>
			{before && !applied && (
				<p className={BEFORE}>
					<span className="sr-only">Before: </span>
					{before}
				</p>
			)}
			{!proposal.delete && (
				<p className={AFTER}>
					<span className="sr-only">After: </span>
					{proposal.lead ? (
						<>
							<strong>{proposal.lead}</strong> {proposal.sentences.join(" ")}
						</>
					) : (
						after
					)}
				</p>
			)}
		</>
	);
};

const MemoEditProposalCard: React.FC<MemoEditProposalCardProps> = ({
	placed,
	pending,
	onApply,
	onOpenSource,
	applyAll = null,
	applied = null,
	applying = false,
	mode = "save",
	baseVersion = null,
}) => {
	const [dismissed, setDismissed] = useState(false);

	if (!placed && applied) {
		return (
			<div
				className="my-2 flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2.5 text-xs font-medium text-[#067647]"
				data-testid="proposal-applied"
			>
				<Check className="h-3.5 w-3.5" aria-hidden />
				Applied. Saved as version {applied.version}.
			</div>
		);
	}
	if (!placed) {
		return (
			<div className="my-2 flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-[#5B6472]">
				{pending ? (
					<>
						<Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
						Writing a suggested change…
					</>
				) : (
					"A suggested change that does not match a section of this memo."
				)}
			</div>
		);
	}

	const { title, summary, problem } = placed;
	// A value names its own source on its own line.
	const sources = placed.kind === "figures" || placed.kind === "company_facts" ? [] : placed.sources;
	return (
		<div
			className="my-2 overflow-hidden rounded-xl border border-gray-200 bg-white font-sans"
			data-testid="memo-edit-proposal"
			data-kind={placed.kind ?? "paragraph"}
			aria-label={`Suggested change to ${title}`}
			role="group"
		>
			<div className="border-b border-[#eef0f2] bg-[#fafafa] px-3 py-2 text-xs font-semibold text-[#5B6472]">
				{title}
				{/* What it would change is news until it is applied. */}
				{applied ? "" : ` · ${summary}`}
			</div>
			<div className="space-y-2 px-3 py-3 font-serif text-[13.5px] leading-relaxed">
				<Body placed={placed} onOpenSource={onOpenSource} applied={applied !== null} />
				{sources.length > 0 && (
					<p className="font-sans text-[11.5px] leading-5 text-[#5B6472]" data-testid="proposal-sources">
						{sources.length === 1 ? "Source: " : "Sources: "}
						{sources.map((source, index) => (
							<React.Fragment key={source.label}>
								{index > 0 && " · "}
								<SourceLink source={source} onOpenSource={onOpenSource} />
							</React.Fragment>
						))}
					</p>
				)}
			</div>
			{applied ? (
				// Said before anything else: once applied, the memo on screen is
				// the version that has the change, so there is nothing left to
				// place it against.
				<p
					className="flex items-center gap-1.5 border-t border-[#eef0f2] px-3 py-2.5 text-xs font-medium text-[#067647]"
					data-testid="proposal-applied"
				>
					<Check className="h-3.5 w-3.5" aria-hidden />
					Applied. Saved as version {applied.version}.
				</p>
			) : problem ? (
				<p className="border-t border-[#eef0f2] px-3 py-2.5 text-xs text-[#b54708]">{problem}</p>
			) : dismissed ? (
				<p className="border-t border-[#eef0f2] px-3 py-2.5 text-xs text-[#6B7280]">Not applied.</p>
			) : (
				<div className="flex flex-wrap items-center gap-2 border-t border-[#eef0f2] px-3 py-2.5">
					<button
						type="button"
						onClick={() => onApply(placed)}
						disabled={pending || applying}
						className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#004C4B] px-3 text-sm font-semibold text-white hover:bg-[#003a3a] disabled:opacity-50"
					>
						{applying && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
						{applying ? "Applying…" : "Apply to memo"}
					</button>
					{applyAll && applyAll.count > 1 && (
						<button
							type="button"
							onClick={applyAll.onApply}
							disabled={pending || applying}
							className="h-8 whitespace-nowrap rounded-lg border border-[#004C4B] bg-white px-3 text-sm font-semibold text-[#004C4B] hover:bg-[#e6f3f3] disabled:opacity-50"
						>
							Apply all {applyAll.count} changes
						</button>
					)}
					<button
						type="button"
						onClick={() => setDismissed(true)}
						disabled={applying}
						className="h-8 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-text-strong hover:bg-gray-50 disabled:opacity-50"
					>
						Not now
					</button>
					<span className="basis-full text-[11.5px] text-[#6B7280]">
						{mode === "edit"
							? "You have unsaved edits. This opens in edit mode with them, to save together."
							: `Saves the memo as a new version.${
									baseVersion ? ` Version ${baseVersion} stays as it was.` : ""
								}`}
					</span>
				</div>
			)}
		</div>
	);
};

export default MemoEditProposalCard;
